// Groups everything that feeds a product's public "Learn More" dialog — the
// long-form details text and the image gallery — into one visually-delineated
// section so admins see them as a single unit. The "Learn More details" field
// was previously a plain multiline TextField; it is now a rich-text editor
// (TipTap) opened in a modal so admins have enough space to work and can
// preview the output as it will appear in the public dialog.
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Button, Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { setDraftField } from '../../../store/product-editor-slice.js';
import { renderableHtml, hasContent } from '../../../utils/rtf-helpers.js';
import LearnMoreEditorModal from './LearnMoreEditorModal.jsx';
import ProductImagesSection from './ProductImagesSection.jsx';

const LearnMoreSection = () => {
  const dispatch = useDispatch();
  const draft = useSelector((state) => state.productEditor.draft);
  const [editorOpen, setEditorOpen] = useState(false);

  const rawValue = draft.long_description || null;
  const previewHtml = renderableHtml(rawValue);
  const populated = hasContent(rawValue);

  const handleSave = (jsonString) => {
    dispatch(setDraftField({ field: 'long_description', value: jsonString }));
  };

  return (
    <Box
      component="section"
      aria-labelledby="learn-more-section-heading"
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      <Box sx={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1,
      }}
      >
        <Box>
          <Typography
            id="learn-more-section-heading"
            component="h3"
            variant="subtitle2"
          >
            Learn More content
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Shown in the product&apos;s public &ldquo;Learn More&rdquo; dialog on the form.
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={() => setEditorOpen(true)}
          sx={{ flexShrink: 0, mt: 0.25 }}
        >
          {populated ? 'Edit content' : 'Add content'}
        </Button>
      </Box>

      {/* Compact read-only preview of the current content */}
      {populated ? (
        <Box
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            px: 1.5,
            py: 1,
            bgcolor: 'background.default',
            fontSize: '0.85rem',
            lineHeight: 1.5,
            color: 'text.secondary',
            maxHeight: 120,
            overflow: 'hidden',
            position: 'relative',
            // Fade-out at the bottom to hint at overflow without a scrollbar.
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: 32,
              background: (theme) => `linear-gradient(transparent, ${theme.palette.background.default})`,
            },
            '& p': { mt: 0, mb: 0.5 },
            '& h2, & h3': { mt: 0.5, mb: 0.25, fontWeight: 700 },
            '& ul, & ol': { pl: 2.5, mb: 0.5 },
          }}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: previewHtml }}
        />
      ) : (
        <Typography
          variant="body2"
          color="text.disabled"
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            px: 1.5,
            py: 1,
            bgcolor: 'background.default',
          }}
        >
          No content yet. Click &ldquo;Add content&rdquo; to write the Learn More details.
        </Typography>
      )}

      <ProductImagesSection />

      <LearnMoreEditorModal
        open={editorOpen}
        value={rawValue}
        onClose={() => setEditorOpen(false)}
        onSave={handleSave}
      />
    </Box>
  );
};

export default LearnMoreSection;
